<template>
  <!--
    Shown when a user clicks "Choose" on a predefined gallery item published
    by an integration that isn't currently active on this shop. The user
    can either install the integration (redirects to marketplace) or cancel
    and stay on the picker.
  -->
  <Dialog
    v-model:visible="visibleLocal"
    :modal="true"
    :draggable="false"
    :closable="true"
    :style="{ width: '480px', maxWidth: '90vw' }"
    class="require-integration-dialog"
  >
    <template #header>
      <div class="rid-header">
        <i class="fa-light fa-plug" />
        <span>{{ t('integrations.requireDialog.title') }}</span>
      </div>
    </template>

    <div
      v-if="integration"
      class="rid-body"
    >
      <div class="rid-integration">
        <div
          class="rid-icon-wrap"
          :class="{ 'rid-icon-wrap--placeholder': !integration.icon_url }"
        >
          <img
            v-if="integration.icon_url"
            :src="integration.icon_url"
            :alt="integration.name"
          >
          <i
            v-else
            class="fa-solid fa-puzzle-piece"
          />
        </div>
        <div class="rid-meta">
          <div class="rid-name">
            {{ integration.name }}
          </div>
          <p
            v-if="integration.short_description"
            class="rid-short"
          >
            {{ integration.short_description }}
          </p>
        </div>
      </div>

      <p class="rid-body-text">
        {{ statusLabel === 'inactive'
          ? t('integrations.requireDialog.bodyInactive', { name: integration.name })
          : t('integrations.requireDialog.bodyNotInstalled', { name: integration.name })
        }}
      </p>
    </div>

    <!--
      Cul-de-sac de gating : quand la marketplace ShopiMind est masquée
      (`MARKETPLACE_ENABLED === false`), on ne renvoie pas l'utilisateur vers
      une page « Bientôt disponible » — on remplace directement le CTA d'action
      par un bouton désactivé « Bientôt disponible » assorti d'une phrase courte.
    -->
    <p
      v-if="integration && !marketplaceEnabled"
      class="rid-soon-note"
    >
      {{ t('integrations.comingSoon.subtitle') }}
    </p>

    <template #footer>
      <div class="rid-footer">
        <Button
          :label="t('integrations.requireDialog.cancel')"
          text
          class="p-button-secondary"
          @click="onCancel"
        />
        <Button
          v-if="marketplaceEnabled"
          :label="statusLabel === 'inactive'
            ? t('integrations.requireDialog.activateCta')
            : t('integrations.requireDialog.installCta')"
          icon="fa-light fa-arrow-right"
          icon-pos="right"
          @click="onInstall"
        />
        <Button
          v-else
          :label="t('integrations.comingSoon.badge')"
          icon="fa-light fa-clock"
          icon-pos="right"
          :disabled="true"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, PropType, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
// eslint-disable-next-line import/no-cycle
import { MARKETPLACE_ENABLED } from '@/router';
import type {
  RequireIntegrationInfo,
  ShopIntegrationStatus,
} from '@/types/integrations';

// The dialog only deals with non-active states — `active` is filtered out
// upstream by `gatePreflight`. The narrower alias keeps the prop typed
// precisely.
export type RequireIntegrationStatus = Exclude<ShopIntegrationStatus, 'active'>;

// Re-exported for backwards compatibility with existing imports.
export type { RequireIntegrationInfo };

export default defineComponent({
  name: 'RequireIntegrationDialog',

  components: {
    Dialog,
    Button,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },

    integration: {
      type: Object as PropType<RequireIntegrationInfo | null>,
      default: null,
    },

    statusLabel: {
      type: String as PropType<RequireIntegrationStatus>,
      default: 'not_installed',
    },
  },

  emits: ['update:visible'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const router = useRouter();

    const visibleLocal = computed({
      get: () => props.visible,
      set: (val: boolean) => emit('update:visible', val),
    });

    const onCancel = () => {
      visibleLocal.value = false;
    };

    const onInstall = () => {
      // Garde-fou : quand la marketplace est masquée le CTA est déjà désactivé,
      // mais on double-vérifie côté logique pour ne jamais naviguer par erreur.
      if (!MARKETPLACE_ENABLED) return;
      // Close the dialog before navigating so it doesn't briefly flash on
      // the marketplace page if the route resolves synchronously.
      visibleLocal.value = false;
      // The marketplace page lists the integration; users land there and
      // click "Install" from the catalogue card. We pre-filter by slug to
      // help them find it instantly.
      router.push({
        name: 'shop.Integrations',
        query: {
          focus: props.integration?.slug ?? undefined,
          focusName: props.integration?.name ?? undefined,
        },
      });
    };

    return {
      t,
      visibleLocal,
      onCancel,
      onInstall,
      marketplaceEnabled: MARKETPLACE_ENABLED,
    };
  },
});
</script>

<style>
.require-integration-dialog .rid-header {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  color: #1f2937;
}
.require-integration-dialog .rid-header i {
  color: #6366f1;
  font-size: 1.1rem;
}

.require-integration-dialog .rid-body {
  padding: 0;
}

.require-integration-dialog .rid-integration {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 16px;
}

.require-integration-dialog .rid-icon-wrap {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.require-integration-dialog .rid-icon-wrap--placeholder {
  background: linear-gradient(135deg, #94c840 0%, #7fb32e 100%);
  border: none;
  color: white;
}

.require-integration-dialog .rid-icon-wrap img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 6px;
}

.require-integration-dialog .rid-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.require-integration-dialog .rid-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
}

.require-integration-dialog .rid-short {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.4;
}

.require-integration-dialog .rid-body-text {
  font-size: 0.85rem;
  color: #374151;
  line-height: 1.5;
  margin: 0;
}

.require-integration-dialog .rid-soon-note {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.5;
  margin: 12px 0 0;
  font-style: italic;
}

.require-integration-dialog .rid-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>
